// src/components/AssessmentView.jsx

import React, { useMemo } from 'react';
import { calculateScores } from '../utils/scoring';
// FIXED: Removed Wrench, FastForward, Brain, CheckCircle2 (Moved to DevToolbar)
import { Layout, ShieldCheck, ChevronRight } from 'lucide-react';
import { useAssessmentLogic } from '../hooks/useAssessmentLogic';
import { isSuperAdmin } from '../config/permissions'; // <--- NEW IMPORT

// SUB-COMPONENTS
import AssessmentEngine from './AssessmentEngine';
import InteractiveRadar from './assessment/InteractiveRadar';
import AssessmentAgent from './assessment/AssessmentAgent';
import RoadSignTest from './assessment/RoadSignTest';
import { AssessmentGate } from './assessment/AssessmentScreens';
import AssessmentComplete from './assessment/AssessmentComplete';
import DevToolbar from './assessment/DevToolbar';
import AssessmentStart from './assessment/AssessmentStart';

// FIXED: Removed local SUPER_ADMIN_EMAILS definition

const AssessmentView = ({ navigateTo, currentUser, onAskAi, onCoachReveal, isPulseMode }) => {
    
    // Pass it to the hook
    const {
        stage, setStage, config, sessionId, agentContext, setAgentContext,
        activeQuestion, baselineAnswers, radarScores, setRadarScores,
        triangulatedScores, submittedStats, handleNextStage, handleGameComplete, 
        handleStepChange, handleSubmit
    } = useAssessmentLogic(currentUser, isPulseMode);

    // Fresh scores for the completion screen (same engine as the dashboard).
    const completionScores = useMemo(
        () => (submittedStats ? calculateScores({ ...(currentUser || {}), latestStats: submittedStats }) : null),
        [submittedStats, currentUser]
    );

    // FIXED: Use centralized check
    const userIsSuperAdmin = isSuperAdmin(currentUser);

    // The way in is one screen that is also the loading screen: it renders at
    // once and enables Start when the config lands (assessment/AssessmentStart.jsx).
    if (stage === 'booting' || stage === 'loading' || !config || stage === 'intro') {
        return (
            <div className="h-full bg-white overflow-hidden lg:rounded-3xl relative">
                <AssessmentStart
                    loading={stage !== 'intro' || !config}
                    config={config}
                    currentUser={currentUser}
                    isPulseMode={isPulseMode}
                    onStart={() => handleNextStage()}
                />
                {userIsSuperAdmin && config && (
                    <DevToolbar setStage={setStage} setRadarScores={setRadarScores} setAgentContext={setAgentContext} />
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row h-full bg-slate-50 overflow-hidden lg:rounded-3xl relative">

            {/* LEFT PANEL: MAIN INTERACTION */}
            <div className="flex-1 relative bg-white flex flex-col overflow-hidden lg:border-r border-slate-100 order-1 lg:order-1">
                <div className="w-full p-4 flex justify-between items-center z-10 bg-white/95 backdrop-blur-sm border-b border-slate-50 sticky top-0 shrink-0">
                    <div className="flex items-center gap-2">
                        <Layout size={18} className="text-brand-primary" />
                        <span className="font-bold text-slate-900 tracking-tight text-xs md:text-base uppercase">{isPulseMode ? 'Weekly check-in' : 'Your check-in'}</span>
                    </div>
                </div>

                <div className="flex-1 relative overflow-hidden">
                    {stage === 'baseline' && (
                        <div className="h-full w-full overflow-hidden">
                            <AssessmentEngine key={config.id} config={config} onComplete={handleNextStage} onStepChange={handleStepChange} />
                        </div>
                    )}

                    {stage === 'radar' && (
                        <div className="flex flex-col h-full overflow-hidden">
                            <div className="px-4 pt-4 md:px-8 shrink-0">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Rate yourself</p>
                                <p className="text-sm text-slate-600 mt-1">Drag each point to where you are right now. The centre is struggling, the edge is thriving.</p>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center justify-center">
                                <div className="w-full max-w-2xl aspect-square shrink-0">
                                    <InteractiveRadar initialValues={radarScores} onUpdate={setRadarScores} />
                                </div>
                            </div>
                            <div className="p-4 bg-white border-t border-slate-100 shrink-0 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                                    <ShieldCheck size={14} className="text-slate-300"/>
                                    <span>Only you see this</span>
                                </div>
                                <button onClick={() => handleNextStage()} className="px-6 py-3 bg-brand-primary text-white rounded-xl font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-md text-sm">
                                    That's me <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}

                    {stage === 'game' && (
                        <div className="flex flex-col h-full items-center justify-center animate-in zoom-in-95 duration-500">
                            <RoadSignTest onComplete={handleGameComplete} />
                        </div>
                    )}

                    {(stage === 'clinical_gate' || stage === 'clinical_active') && (
                        <AssessmentGate stage={stage} onNext={() => handleNextStage()} onSkip={() => handleSubmit()} />
                    )}

                    {stage === 'complete' && (
                        <AssessmentComplete 
                            onNavigate={navigateTo} 
                            onAskAi={onAskAi} 
                            onCoachReveal={onCoachReveal}
                            targetMetric={agentContext?.targetMetric} 
                            scores={completionScores}
                            currentUser={currentUser}
                            isPulseMode={isPulseMode}
                            audience={config?.audience}
                        />
                    )}
                </div>
            </div>

            {/* RIGHT PANEL: the coach conversation. Only when the gate asks for it and
                while it runs; the questions and the self-rating take the full width. */}
            {(stage === 'clinical_gate' || stage === 'clinical_active') && (
            <div className={`
                ${stage === 'clinical_active' ? 'h-[55%] lg:w-[380px]' : 'h-auto lg:w-[300px]'}
                lg:h-auto
                bg-slate-50 shrink-0 relative z-20 border-t lg:border-t-0 lg:border-l border-slate-200
                shadow-[0_-4px_20px_rgba(0,0,0,0.05)] lg:shadow-none order-2 lg:order-2 flex flex-col
                transition-all duration-300 ease-in-out
            `}>
                <div className={`${stage === 'clinical_active' ? 'flex-1 min-h-0' : 'lg:flex-1 lg:min-h-0'} flex flex-col overflow-hidden`}>
                    <AssessmentAgent 
                        stage={stage} 
                        onNext={handleNextStage} 
                        onSkip={() => handleSubmit()} 
                        context={agentContext}
                        activeQuestion={activeQuestion} 
                        onContextData={{ 
                            radarScores: Object.keys(triangulatedScores).length > 0 ? triangulatedScores : radarScores, 
                            baselineAnswers 
                        }} 
                        sessionId={sessionId}
                    />
                </div>
            </div>
            )}

            {/* DEV TOOLBAR (Single Instance) */}
            {userIsSuperAdmin && (
                <DevToolbar 
                    setStage={setStage} 
                    setRadarScores={setRadarScores} 
                    setAgentContext={setAgentContext} 
                />
            )}
        </div>
    );
};

export default AssessmentView;